import { useEffect, useMemo, useState } from 'react';
import { Box, Card, Divider, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { LenderSelect, ListHeader, ListRow, ToolbarContainer, dataModelIcons } from '/src/components';
import { useFilterStore, useStatementStore, useUserStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Lender } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';

/**
 * List Lender Statements Page
 */
export const ListLenderStatementsPage = observer(() => {
  const defaultSortType = 'effectiveDate';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const statementStore = useStatementStore();
  const userStore = useUserStore();
  const filterStore = useFilterStore();

  /** State **/
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [lender, setLender] = useState<Lender>();

  /** Computed **/
  const statements = statementStore.statements;

  const listData = useMemo(() => {
    if (statements) {
      const list = statements?.toSorted((a, b) => (get(a, sortType) ?? 0) - (get(b, sortType) ?? 0));
      if (list && sortReverse) list.reverse();

      return list;
    }
  }, [statements, sortType, sortReverse]);

  const lenders = useMemo(() => {
    const l = filterStore.lenderListGet();
    return Array.isArray(l) ? l : l && l._id ? [l] : [];
  }, [filterStore._lenderList]);

  /** Methods **/

  /**
   * Sets sortType and sortReverse based on given sortType.
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (lender?._id) statementStore.queryLenderStatements(lender._id);

    return () => (statementStore.statements = undefined);
  }, [lender]);

  /** Render **/

  return (
    <DesktopLayout>
      <Box id="listLenderStatements" component="md-content" className="md-default-theme">
        <Card component="md-card">
          <ToolbarContainer>
            <Box display="flex" flex={33} />

            <Box justifyContent="center" alignItems="center" display="flex">
              {dataModelIcons[DataModelType.Statement]}
              <Box component="span" className="md-title pad-left-xs">
                {`${userStore.isAdmin ? 'Investor ' : ''} Statements`}
              </Box>
            </Box>

            <Box display="flex" flex={33} />
          </ToolbarContainer>

          <Box bgcolor="white">
            <Box display="flex" justifyContent="center" alignItems="center" gap="32px" paddingX="16px">
              <LenderSelect
                value={lender?._id ?? ''}
                setValue={(lenderId) => {
                  const lender = lenders.find((lender) => lender._id === lenderId);
                  setLender(lender);
                  if (!lender) statementStore.statements = undefined;
                }}
                lenders={lenders}
                ariaLabel="Select an Investor to view their Statements."
                boxProps={{ width: '100%' }}
                className="md-primary no-margin-top-bottom"
              />
            </Box>
          </Box>

          {(lender?._id || !!listData?.length) && <Divider />}

          {/* LIST */}
          {lender?._id && !!lender.participations.length && !!listData?.length && (
            <Box bgcolor="white">
              <ListHeader
                items={[{ propName: 'effectiveDate', label: 'Statement Date' }]}
                sortType={sortType}
                sortReverse={sortReverse}
                sortList={sortList}
              />
              <Divider />
              <Box
                //  className={listData.length > 11 ? 'fixed-list-lg' : ''}
                sx={{ maxHeight: `${(11 * rowHeight).toString()}px` }}
              >
                {listData?.map((statement, i) => (
                  <ListRow
                    key={statement._id}
                    items={[
                      {
                        propName: 'effectiveDate',
                        content: dayjs(statement.effectiveDate).format('MMMM YYYY'),
                        url: `/statements/investors/${lender?._id}/${dayjs(statement.effectiveDate).format('YYYY-M')}`,
                        linkTarget: '_blank',
                      },
                    ]}
                    rowIndex={i}
                  />
                ))}
              </Box>
            </Box>
          )}

          {lender && statements && !statements?.length && (
            <ListItem sx={{ bgcolor: 'white' }}>
              <Box
                display="flex"
                flex="100"
                justifyContent="center"
                alignItems="center"
                className="pad-top-bottom md-title thin"
              >
                No Statements Found.
              </Box>
            </ListItem>
          )}
        </Card>
      </Box>
    </DesktopLayout>
  );
});
